/**
 * Backend Tour Service - Uses the new PostgreSQL backend API
 * This service is used when REACT_APP_USE_BACKEND_API=true
 * Main branch will continue using Supabase (databaseTourService)
 *
 * Caching Layer (in priority order):
 *   [1] staticDataService  – JSON baked into Vercel CDN at build-time (0 ms)
 *   [2] In-memory cache     – already in this service instance
 *   [3] localStorage cache  – persisted from a previous session
 *   [4] Live backend API    – silent SWR revalidation in the background
 */

import { Tour } from '../types';
import { API_BASE_URL } from '../config/apiConfig';
import { apiService } from './apiService';
import { normalizeImageUrls } from '../utils/normalizeImageUrl';
import { staticDataService } from './staticDataService';

class BackendTourService {
  private cache: Tour[] = [];
  private cacheTimestamp: number = 0;
  private readonly CACHE_DURATION = 60 * 60 * 1000; // 1 hour for performance
  private readonly CACHE_VERSION = 'v2.0'; // v2.0: forced clear to remove ghost categories
  private readonly STORAGE_KEY = 'tours_cache_backend';
  private readonly STORAGE_TIMESTAMP_KEY = 'tours_cache_backend_timestamp';
  private readonly STORAGE_URL_KEY = 'tours_cache_backend_url';
  private readonly STORAGE_VERSION_KEY = 'tours_cache_backend_version';
  private readonly FULL_TOUR_CACHE_KEY = 'tours_full_cache_backend';
  private fullToursCache: Map<string, Tour> = new Map();
  private pendingRequest: Promise<Tour[]> | null = null;
  private pendingTourRequests: Map<string, Promise<Tour | null>> = new Map();

  public hasCache(): boolean {
    return this.cache && this.cache.length > 0;
  }

  public getCache(): Tour[] {
    return [...this.cache];
  }

  private loadCacheFromStorage(): void {
    try {
      const cachedData = localStorage.getItem(this.STORAGE_KEY);
      const cachedTimestamp = localStorage.getItem(this.STORAGE_TIMESTAMP_KEY);
      const cachedUrl = localStorage.getItem(this.STORAGE_URL_KEY);
      const cachedVersion = localStorage.getItem(this.STORAGE_VERSION_KEY);
      const currentUrl = API_BASE_URL;

      if (cachedData && cachedTimestamp) {
        if (cachedVersion !== this.CACHE_VERSION) {
          this.clearCache();
          return;
        }

        if (cachedUrl && cachedUrl !== currentUrl) {
          this.clearCache();
          return;
        }

        const timestamp = parseInt(cachedTimestamp, 10);
        const parsed = JSON.parse(cachedData);
        this.cache = this.deduplicateTours(Array.isArray(parsed) ? parsed : []);
        this.cacheTimestamp = timestamp;
      }
    } catch (error) {
      console.error('Error loading cache from localStorage:', error);
    }

    try {
      const fullCachedData = localStorage.getItem(this.FULL_TOUR_CACHE_KEY);
      if (fullCachedData) {
        const parsed = JSON.parse(fullCachedData);
        this.fullToursCache = new Map(Object.entries(parsed));
      }
    } catch (error) {
      console.error('Error loading full tours cache:', error);
    }
  }

  private saveFullToursCacheToStorage(): void {
    try {
      const obj = Object.fromEntries(this.fullToursCache);
      localStorage.setItem(this.FULL_TOUR_CACHE_KEY, JSON.stringify(obj));
    } catch (error) {
      console.error('Error saving full tours cache:', error);
    }
  }

  private saveCacheToStorage(tours: Tour[]): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(tours));
      localStorage.setItem(this.STORAGE_TIMESTAMP_KEY, Date.now().toString());
      localStorage.setItem(this.STORAGE_URL_KEY, API_BASE_URL);
      localStorage.setItem(this.STORAGE_VERSION_KEY, this.CACHE_VERSION);
    } catch (error) {
      console.error('Error saving cache to localStorage:', error);
    }
  }

  constructor() {
    this.loadCacheFromStorage();
  }

  async getAllTours(forceRefresh: boolean = false): Promise<Tour[]> {
    // 1. Static CDN Cache – instant, zero backend dependency
    if (!forceRefresh) {
      const staticTours = staticDataService.getAllTours();
      if (staticTours.length > 0) {
        // Populate our in-memory + localStorage cache from the static snapshot
        if (this.cache.length === 0) {
          const normalized = staticTours.map(t => this.normalizeTour(t));
          const sorted = [...normalized].sort((a, b) => {
            const aO = (a as any).sortOrder ?? 9999;
            const bO = (b as any).sortOrder ?? 9999;
            return aO - bO;
          });
          this.cache = this.deduplicateTours(sorted);
          this.cacheTimestamp = Date.now();
          this.saveCacheToStorage(this.cache);
        }
        // Revalidate live data silently in the background (SWR)
        this.fetchToursFromBackend(true).catch(() => { });
        return [...this.cache];
      }
    }

    // 2. In-memory cache (already populated this session)
    if (!forceRefresh && this.cache && this.cache.length > 0) {
      const cacheAge = Date.now() - this.cacheTimestamp;
      if (cacheAge > 60 * 60 * 1000) {
        console.log('🔄 Cache stale (1hr+), revalidating tours in background...');
        this.fetchToursFromBackend(true).catch(() => { });
      }
      return [...this.cache];
    }

    if (forceRefresh) {
      this.clearCache();
    }

    if (this.pendingRequest) {
      return await this.pendingRequest;
    }

    this.pendingRequest = this.fetchToursFromBackend(false);
    try {
      const tours = await this.pendingRequest;
      this.pendingRequest = null;
      return tours || [];
    } catch (error) {
      this.pendingRequest = null;
      if (!this.cache || this.cache.length === 0) {
        this.loadCacheFromStorage();
      }
      return this.cache ? [...this.cache] : [];
    }
  }

  private async fetchToursFromBackend(silent: boolean = false): Promise<Tour[]> {
    try {
      const tours = await apiService.getAllTours(false);

      if (!tours) {
        return this.cache || [];
      }

      const normalizedTours = tours.map(t => this.normalizeTour(t));
      const deduplicated = this.deduplicateTours(normalizedTours);

      // Always sort by sort_order (featured order) client-side after fetch
      const sortedTours = [...deduplicated].sort((a, b) => {
        const aOrder = (a as any).sortOrder ?? (a as any).sort_order ?? 9999;
        const bOrder = (b as any).sortOrder ?? (b as any).sort_order ?? 9999;
        return aOrder - bOrder;
      });

      this.cache = sortedTours;
      this.cacheTimestamp = Date.now();
      this.saveCacheToStorage(sortedTours);

      // Push fresh live data into the static cache layer (SWR hydration)
      staticDataService.hydrateLiveTours(sortedTours);

      if (!silent) {
        window.dispatchEvent(new Event('toursUpdated'));
      }

      return normalizedTours;
    } catch (error: any) {
      console.error('❌ Error fetching tours from backend:', error?.message || error);
      if (this.cache && this.cache.length > 0) {
        return [...this.cache];
      }
      // Last resort: return the static snapshot if available
      const staticTours = staticDataService.getAllTours();
      if (staticTours.length > 0) return staticTours.map(t => this.normalizeTour(t));
      return [];
    }
  }

  async getTourById(id: string): Promise<Tour | null> {
    // 1. Check Full Detail Cache (Dedicated)
    const fullyCached = this.fullToursCache.get(id);
    if (fullyCached) {
      // Revalidate in background
      this.fetchTourByIdFromBackend(id).catch(() => { });
      return fullyCached;
    }

    // 2. Check Listing Cache (Partial)
    const listingCached = this.cache.find(tour => tour.id === id);
    if (listingCached) {
      // Return partial but trigger full fetch
      this.fetchTourByIdFromBackend(id).catch(() => { });
      return listingCached;
    }

    // 3. Check Pending Requests
    if (this.pendingTourRequests.has(id)) {
      return await this.pendingTourRequests.get(id)!;
    }

    // 4. Fetch from Backend
    return await this.fetchTourByIdFromBackend(id);
  }

  // Alias - always hits the API directly, never serves listing cache
  async getFullTourById(id: string): Promise<Tour | null> {
    // 1. Check Full Detail Cache (Dedicated)
    const fullyCached = this.fullToursCache.get(id);
    if (fullyCached) {
      // Revalidate in background
      this.fetchTourByIdFromBackend(id).catch(() => { });
      return fullyCached;
    }

    // 2. Check Pending Requests
    if (this.pendingTourRequests.has(id)) {
      return await this.pendingTourRequests.get(id)!;
    }

    // 3. Fetch from Backend
    return await this.fetchTourByIdFromBackend(id);
  }

  private async fetchTourByIdFromBackend(id: string): Promise<Tour | null> {
    if (this.pendingTourRequests.has(id)) {
      return await this.pendingTourRequests.get(id)!;
    }

    const promise = (async () => {
      try {
        const tour = await apiService.getTourById(id);
        if (tour) {
          const normalizedTour = this.normalizeTour(tour);
          
          // Update both caches
          this.fullToursCache.set(id, normalizedTour);
          this.saveFullToursCacheToStorage();
          
          const existingIndex = this.cache.findIndex(t => t.id === id);
          if (existingIndex >= 0) {
            this.cache[existingIndex] = normalizedTour;
          } else {
            this.cache.push(normalizedTour);
          }
          
          this.cache = this.deduplicateTours(this.cache);
          this.saveCacheToStorage(this.cache);
          this.cacheTimestamp = Date.now();
          window.dispatchEvent(new CustomEvent(`tourUpdated:${id}`, { detail: normalizedTour }));
          return normalizedTour;
        }
        return null;
      } catch (error) {
        console.error(`❌ fetchTourByIdFromBackend failed for ${id}:`, error);
        return null;
      } finally {
        this.pendingTourRequests.delete(id);
      }
    })();

    this.pendingTourRequests.set(id, promise);
    return await promise;
  }

  async getToursByCategoryId(categoryId: string): Promise<Tour[]> {
    // Helper: match tour to category regardless of snake_case vs camelCase field name
    const matchesCategory = (t: any) =>
      (t as any).categoryId === categoryId ||
      (t as any).category_id === categoryId;

    // Check if we have the main tours cache - filter from it for instant results
    if (this.cache && this.cache.length > 0) {
      const filtered = this.cache.filter(matchesCategory);

      // Always fetch fresh from backend in the background (SWR) —
      // critical for newly-added tours that aren't in the static snapshot
      // AND for tours whose category was just changed.
      apiService.getToursByCategoryId(categoryId)
        .then(tours => {
          if (!tours) return;
          const normalized = tours.map(t => this.normalizeTour(t));

          // Merge fresh tours into the main cache (add missing, update existing)
          normalized.forEach(nt => {
            const idx = this.cache.findIndex(c => c.id === nt.id);
            if (idx !== -1) this.cache[idx] = nt;
            else this.cache.push(nt);
          });

          // Remove tours from this category slot that are no longer in the
          // live result — i.e. tours whose category was reassigned away.
          const freshIds = new Set(normalized.map(t => t.id));
          this.cache = this.cache.map(cached => {
            // If this tour WAS in this category but is NOT in the live result,
            // clear its categoryId so it won't falsely appear here again.
            if (
              matchesCategory(cached) &&
              !freshIds.has(cached.id)
            ) {
              // Don't delete from global cache; just nullify the stale categoryId
              return { ...cached, categoryId: undefined, category_id: undefined } as any;
            }
            return cached;
          });

          this.cache = this.deduplicateTours(this.cache);
          this.saveCacheToStorage(this.cache);

          // Notify the UI so it re-renders with the fresh, complete list
          window.dispatchEvent(new CustomEvent('categoryToursUpdated', {
            detail: { categoryId, tours: normalized }
          }));
        })
        .catch(() => { });

      // Return what we have instantly (may be empty if category is new)
      if (filtered.length > 0) return filtered;
    }

    // No cache or empty filter result — must await live API
    try {
      const tours = await apiService.getToursByCategoryId(categoryId);
      const normalized = tours.map(t => this.normalizeTour(t));
      normalized.forEach(nt => {
        const idx = this.cache.findIndex(c => c.id === nt.id);
        if (idx !== -1) this.cache[idx] = nt;
        else this.cache.push(nt);
      });
      this.cache = this.deduplicateTours(this.cache);
      this.saveCacheToStorage(this.cache);
      return normalized;
    } catch (error) {
      console.error('❌ Error fetching tours by category from backend:', error);
      return [];
    }
  }


  private deduplicateTours(tours: Tour[]): Tour[] {
    if (!tours || !Array.isArray(tours)) return [];
    const seen = new Set<string>();
    return tours.filter(tour => {
      if (!tour || !tour.id) return false;
      if (seen.has(tour.id)) {
        console.warn(`🚨 Duplicate tour found and removed from cache: ${tour.id}`);
        return false;
      }
      seen.add(tour.id);
      return true;
    });
  }

  clearCache(): void {
    this.cache = [];
    this.cacheTimestamp = 0;
    this.fullToursCache.clear();
    localStorage.removeItem(this.STORAGE_KEY);
    localStorage.removeItem(this.STORAGE_TIMESTAMP_KEY);
    localStorage.removeItem(this.STORAGE_URL_KEY);
    localStorage.removeItem(this.STORAGE_VERSION_KEY);
    localStorage.removeItem(this.FULL_TOUR_CACHE_KEY);
  }

  async refreshCache(): Promise<Tour[]> {
    this.clearCache();
    return await this.fetchToursFromBackend();
  }

  async getTourStats(): Promise<{
    totalTours: number;
    activeTours: number;
    totalRevenue: number;
    avgRating: number;
  }> {
    try {
      return await apiService.getTourStats();
    } catch (error) {
      const tours = this.cache.length > 0 ? this.cache : await this.getAllTours();
      const activeTours = tours.filter(tour => tour.active !== false);
      const totalRevenue = tours.reduce((sum, tour) => sum + (Number(tour.price) || 0), 0);
      const avgRating = tours.length > 0
        ? tours.reduce((sum, tour) => sum + (Number(tour.rating) || 0), 0) / tours.length
        : 0;

      return {
        totalTours: tours.length,
        activeTours: activeTours.length,
        totalRevenue,
        avgRating: Math.round(avgRating * 10) / 10,
      };
    }
  }

  async getTourReviews(tourId: string): Promise<any[]> {
    try {
      return await apiService.getTourReviews(tourId);
    } catch (error) {
      return [];
    }
  }

  async addReview(tourId: string, reviewData: {
    userName: string;
    rating: number;
    title: string;
    comment: string;
  }): Promise<any> {
    return await apiService.addReview(tourId, reviewData);
  }

  async updateTourRating(tourId: string): Promise<void> {
    try {
      const tour = await this.getTourById(tourId);
      if (tour) {
        const index = this.cache.findIndex(t => t.id === tourId);
        if (index !== -1) {
          this.cache[index] = tour;
          this.saveCacheToStorage(this.cache);
        }
      }
    } catch (error) {
      console.error('Error updating tour rating:', error);
    }
  }

  private normalizeTour(tour: any): Tour {
    if (!tour) return {} as Tour;
    const t = tour as any;

    // Robust JSON parsing for all data fields
    const parseJson = (val: any, fallback: any = null) => {
      if (val === null || val === undefined) return fallback;
      if (Array.isArray(val) || typeof val === 'object') return val;
      if (typeof val === 'string') {
        try {
          return JSON.parse(val);
        } catch (e) {
          return val;
        }
      }
      return val;
    };

    // Guide mapping: Standardize and prioritize populated fields
    const isEmpty = (val: any) => !val || (Array.isArray(val) && val.length === 0) || (typeof val === 'object' && Object.keys(val).length === 0);
    
    let normalizedGuides: any[] = [];
    const tGuides = parseJson(t.guides);
    const tGuide = parseJson(t.guide);
    
    // Prioritize whichever one is not empty
    let rawGuides = [];
    if (!isEmpty(tGuides)) {
      rawGuides = tGuides;
    } else if (!isEmpty(tGuide)) {
      rawGuides = tGuide;
    }
    
    const parsedGuides = parseJson(rawGuides);
    
    if (Array.isArray(parsedGuides)) {
      normalizedGuides = parsedGuides;
    } else if (parsedGuides) {
      normalizedGuides = [parsedGuides];
    }

    normalizedGuides = normalizedGuides.map((g: any) => {
      if (!g) return null;
      if (typeof g === 'string') return { id: g, firstName: 'Expert', lastName: 'Guide' };
      if (g.name && (!g.firstName || !g.lastName)) {
        const parts = g.name.trim().split(' ');
        return { ...g, firstName: parts[0] || '', lastName: parts.slice(1).join(' ') || '' };
      }
      return g;
    }).filter(Boolean);

    // Extract tiered pricing for fallback
    const rawTiers = parseJson(t.tierVariants || t.tier_variants) || {};
    const rawDiscounts = parseJson(t.discountedTierVariants || t.discounted_tier_variants) || {};

    // Normalize keys to uppercase to match PricingTier enum
    const tiers: any = {};
    Object.keys(rawTiers).forEach(key => {
      tiers[key.toUpperCase()] = rawTiers[key];
    });

    const discounts: any = {};
    Object.keys(rawDiscounts).forEach(key => {
      discounts[key.toUpperCase()] = rawDiscounts[key];
    });

    // Normalize price with robust fallback to tiered data
    let tourPrice = typeof t.price === 'number' ? t.price : (typeof t.price === 'object' && t.price?.toNumber ? t.price.toNumber() : (t.price ? parseFloat(t.price) : 0));
    
    // If price is 0 or null, calculate from tiers
    if (!tourPrice || tourPrice === 0) {
      const findMin = (obj: any) => {
        let min = Infinity;
        if (!obj || typeof obj !== 'object') return min;
        Object.keys(obj).forEach(pk => {
          const tier = obj[pk];
          if (!tier || typeof tier !== 'object') return;
          ['international', 'local'].forEach(l => {
            const r = tier[l];
            if (r && (r.adult || r.adultBase)) {
              const v = parseFloat(r.adult || r.adultBase);
              if (v > 0 && v < min) min = v;
            }
          });
        });
        return min;
      };
      
      const minBase = findMin(tiers);
      const minDisc = findMin(discounts);
      const baseToUse = Math.min(minBase, minDisc);
      
      if (baseToUse !== Infinity) {
        const margin = parseFloat(t.margin_percentage || t.marginPercentage || 0);
        tourPrice = baseToUse * (1 + margin / 100);
      }
    }

    return {
      ...t,
      price: tourPrice,
      images: (() => {
        const normalized = normalizeImageUrls(Array.isArray(t.images) ? t.images : (parseJson(t.images) || []));
        return normalized.length > 0 ? normalized : ['/images/placeholder-tour.jpg'];
      })(),
      shortDescription: t.shortDescription || t.short_description || '',
      maxParticipants: t.maxParticipants || t.max_participants || 12,
      minParticipants: t.minParticipants || t.min_participants || 2,
      reviewCount: t.reviewCount || t.review_count || 0,
      itinerary: parseJson(t.itinerary || t.itinerary_json) || [],
      highlights: parseJson(t.highlights || t.highlights_json) || [],
      includes: parseJson(t.includes) || [],
      excludes: parseJson(t.excludes) || [],
      requirements: parseJson(t.requirements) || [],
      tierVariants: tiers,
      discountedTierVariants: discounts,
      seasonalAdjustments: parseJson(t.seasonalAdjustments || t.seasonal_adjustments) || [],
      extras: parseJson(t.extras) || [],
      guides: normalizedGuides,
      sortOrder: t.sortOrder !== undefined ? t.sortOrder : (t.sort_order !== undefined ? t.sort_order : 0),
      location: (() => {
        const loc = parseJson(t.location);
        if (typeof loc === 'string') return { name: loc };
        if (loc && typeof loc === 'object' && loc.name) return loc;
        return { name: t.location || 'Kenya' };
      })(),
      active: t.active !== undefined ? t.active : (t.status === 'active'),
    } as unknown as Tour;
  }

  private scrubPayload(payload: any): any {
    const scrubbed = { ...payload };

    // Handle empty strings for date fields - convert to null for Postgres
    // Only touch them if they are explicitly passed as empty strings
    const dateFields = ['bookingOpenDate', 'bookingCloseDate', 'safariStartDate', 'safariEndDate'];
    dateFields.forEach(field => {
      if (scrubbed[field] === '') {
        scrubbed[field] = null;
      }
    });

    if (scrubbed.sortOrder !== undefined) {
      scrubbed.sortOrder = Number(scrubbed.sortOrder) || 0;
    }

    // Ensure we don't send undefined fields
    Object.keys(scrubbed).forEach(key => {
      if (scrubbed[key] === undefined) {
        delete scrubbed[key];
      }
    });

    return scrubbed;
  }

  async addTour(tourData: Omit<Tour, 'id' | 'createdAt' | 'updatedAt'>): Promise<Tour> {
    try {
      const scrubbedData = this.scrubPayload(tourData);
      const newTour = await apiService.createTour(scrubbedData);
      this.clearCache();
      return this.normalizeTour(newTour);
    } catch (error) {
      console.error('Error adding tour to backend:', error);
      throw error;
    }
  }

  async updateTour(id: string, tourData: Partial<Tour> & { sortOrder?: number }): Promise<Tour> {
    try {
      const scrubbedData = this.scrubPayload(tourData);
      
      // 1. Perform the API update
      await apiService.updateTour(id, scrubbedData);
      
      // 2. ALWAYS clear ALL cache after update to prevent stale cache from masking DB write failures
      this.clearCache();
      
      // 3. Re-fetch the full tour from the API to confirm the database write actually succeeded
      // This is the source of truth - if data is missing here, the DB write failed
      const confirmedTour = await this.fetchTourByIdFromBackend(id);
      if (!confirmedTour) {
        throw new Error(`Tour ${id} not found after save - DB write may have failed`);
      }
      
      console.log('✅ Tour update confirmed from DB:', id, {
        highlights: Array.isArray(confirmedTour.highlights) ? confirmedTour.highlights.length : 0,
        itinerary: Array.isArray(confirmedTour.itinerary) ? confirmedTour.itinerary.length : 0,
        hasTiers: !!(confirmedTour.tierVariants && Object.keys(confirmedTour.tierVariants).length > 0)
      });
      
      return confirmedTour;
    } catch (error) {
      console.error('Error updating tour in backend:', error);
      throw error;
    }
  }

  async deleteTour(id: string): Promise<boolean> {
    try {
      await apiService.deleteTour(id);
      this.cache = this.cache.filter(t => t.id !== id);
      this.saveCacheToStorage(this.cache);
      return true;
    } catch (error) {
      console.error('Error deleting tour from backend:', error);
      throw error;
    }
  }
}

export const backendTourService = new BackendTourService();
export default backendTourService;
